import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Settings2,
  Bold,
  Italic,
  Underline,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import UnderlineField from '../ui/UnderlineField';
import { toggleTag, type RichTag } from '../../lib/richText';
import OcrFillPanel, { parseOcrText } from './OcrFillPanel';
import {
  QuestionFormat,
  QUESTION_TYPE_PRESET_MAP,
  inferPresetFromQuestion,
} from '../../lib/questionPresets';
import QuestionStemFields from './QuestionStemFields';
import QuestionOptionsFields from './QuestionOptionsFields';
import ExamPreviewFrame from './ExamPreviewFrame';
import LayoutPreviewPicker from './LayoutPreviewPicker';
import { resolveContentBlocks } from '@eps/shared-utils';
import {
  type OptionForm,
  type QuestionForm,
  TYPES,
  CATEGORIES,
  SUBCATEGORY_LABELS,
  DIFFICULTIES,
  QUESTION_LENGTH_TYPES,
  TIERS,
  genGroupId,
  blocksValidationError,
} from '../../lib/questionForm';

interface ParsedBulkPaste {
  questionText: string;
  options: string[];
}

function parseBulkPastedQuestion(raw: string): ParsedBulkPaste {
  const normalized = raw
    .replace(/\r/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/^[ \t]*```[\s\S]*?\n/, '')
    .replace(/\n```[ \t]*$/, '')
    .trim();

  const lines = normalized
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const optionRegex = /^(?:[①②③④]|[1-4][).:]|[A-Da-d][).:])\s*/;
  const optionIndexes = lines
    .map((line, index) => (optionRegex.test(line) ? index : -1))
    .filter((index) => index >= 0);

  if (optionIndexes.length >= 2) {
    const questionLines = lines
      .slice(0, optionIndexes[0])
      .map((line) => line.replace(/^question\s*[:-]\s*/i, ''));
    const options = optionIndexes
      .map((start, index) => {
        const end = optionIndexes[index + 1] ?? lines.length;
        return lines.slice(start, end).join(' ').replace(optionRegex, '').trim();
      })
      .filter(Boolean);

    return {
      questionText: questionLines.join(' ').trim(),
      options: options.slice(0, 4),
    };
  }

  return parseOcrText(normalized);
}

interface QuestionGroup {
  groupId: string;
  prompt: string;
  category?: string;
  ubtLayout?: string;
  count: number;
}

// ── Question Form Fields ────────────────────────────────────────────────────

export default function QuestionFormFields({
  form,
  setForm,
  lockedCategory,
  lockedTypes,
}: {
  form: QuestionForm;
  setForm: (f: QuestionForm) => void;
  /** When set, the Category select is pre-filled and disabled (used when embedding this wizard from a context that already knows the section, e.g. the mock test drawer). */
  lockedCategory?: 'reading' | 'listening';
  /** When set, the Types chips are pre-filled and disabled, same rationale as lockedCategory. */
  lockedTypes?: string[];
}) {
  const [bulkPasteValue, setBulkPasteValue] = useState('');
  /** Two-step wizard: fill question settings first, then preview/build the question content. */
  const [step, setStep] = useState<'settings' | 'preview'>('settings');

  useEffect(() => {
    if (!lockedCategory && !lockedTypes) return;
    const categoryNeedsUpdate = !!lockedCategory && form.category !== lockedCategory;
    const typesNeedUpdate = !!lockedTypes && form.types.join(',') !== lockedTypes.join(',');
    if (!categoryNeedsUpdate && !typesNeedUpdate) return;
    setForm({
      ...form,
      ...(categoryNeedsUpdate ? { category: lockedCategory, subcategory: '' } : {}),
      ...(typesNeedUpdate ? { types: lockedTypes } : {}),
    });
  }, [lockedCategory, lockedTypes?.join(',')]);

  const { data: groupsData } = useQuery({
    queryKey: ['question-groups', form.category],
    queryFn: async () => {
      const res = await api.get('/questions/groups', { params: { category: form.category } });
      return res.data as { data: QuestionGroup[] };
    },
  });
  const groups = groupsData?.data ?? [];
  const NEW_GROUP = '__new__';
  const selectedGroupValue = !form.groupId
    ? ''
    : groups.some((g) => g.groupId === form.groupId)
      ? form.groupId
      : NEW_GROUP;

  const metaSummary = [
    form.category,
    form.subcategory ? (SUBCATEGORY_LABELS[form.subcategory] ?? form.subcategory) : null,
    form.difficulty,
    form.questionLengthType,
    form.tier,
    form.types.join('/').toUpperCase(),
  ]
    .filter(Boolean)
    .join(' · ');

  const initialInference = inferPresetFromQuestion(form.question, form.options);
  const initialPresetConfig = form.questionType
    ? QUESTION_TYPE_PRESET_MAP[form.questionType]
    : null;

  const [optionFormat, setOptionFormat] = useState<QuestionFormat>(
    () => initialPresetConfig?.optionFormat ?? initialInference.optionFormat,
  );

  /**
   * Called after OCR/bulk-paste externally overwrite form.question/options.
   * Updates the *display* optionFormat to match what was just filled, and
   * resyncs form.question.blocks from the legacy fields the caller just set
   * so the block-chip builder reflects the fill. Deliberately does NOT write
   * form.questionType — OCR/bulk-paste output is auto-extracted, not a
   * deliberate admin choice, so it must not silently confirm a specific
   * named type. The caller's own setForm call is responsible for setting
   * questionType: null explicitly.
   */
  const syncFromExternalFill = (
    question: QuestionForm['question'],
    options: QuestionForm['options'],
  ) => {
    const inferred = inferPresetFromQuestion(question, options);
    setOptionFormat(inferred.optionFormat);
    setForm({
      ...form,
      question: {
        ...question,
        blocks: resolveContentBlocks({
          text: question.text,
          imageUrl: question.imageUrl,
          audioUrl: question.audioUrl,
        }),
      },
      options,
    });
  };

  const changeOptionFormat = (fmt: QuestionFormat) => {
    setOptionFormat(fmt);
    setForm({
      ...form,
      questionType: null,
      options: form.options.map((o) => ({
        ...o,
        format: fmt,
        text: fmt === QuestionFormat.TEXT ? o.text : '',
        imageUrl: fmt === QuestionFormat.IMAGE ? o.imageUrl : '',
        audioUrl: fmt === QuestionFormat.AUDIO ? o.audioUrl : '',
      })),
    });
  };

  const updateOption = (idx: number, patch: Partial<OptionForm>) => {
    const opts = [...form.options];
    opts[idx] = { ...opts[idx], ...patch };
    if (patch.isCorrect)
      opts.forEach((o, i) => {
        if (i !== idx) o.isCorrect = false;
      });
    setForm({ ...form, options: opts });
  };

  const toggleType = (type: string) => {
    const nextTypes = form.types.includes(type)
      ? form.types.filter((item) => item !== type)
      : [...form.types, type];
    setForm({ ...form, types: nextTypes.length > 0 ? nextTypes : [type] });
  };

  /**
   * Applies bold/italic/underline markup to whatever text is currently selected
   * inside the preview section (a stem text block or an option field), located via
   * the `data-rt-field="block:<id>"` / `"option:<idx>"` attribute on the focused
   * element. Toolbar buttons preventDefault on mousedown so this element stays
   * focused (and its selection intact) through the click — same trick as UnderlineField.
   */
  const applyTag = (tag: RichTag) => {
    const el = document.activeElement;
    const field = el instanceof HTMLElement ? el.dataset.rtField : undefined;
    if (!field || !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
      toast.error('Select text in the question or an answer first');
      return;
    }
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (start === end) {
      toast.error('Select text to format first');
      return;
    }
    const res = toggleTag(tag, el.value, start, end);
    const [kind, key] = field.split(':');
    if (kind === 'block') {
      setForm({
        ...form,
        question: {
          ...form.question,
          blocks: form.question.blocks.map((b) =>
            b.id === key && b.type === 'text' ? { ...b, text: res.value } : b,
          ),
        },
      });
    } else {
      const idx = Number(key);
      setForm({
        ...form,
        options: form.options.map((o, i) => (i === idx ? { ...o, text: res.value } : o)),
      });
    }
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(res.selectionStart, res.selectionEnd);
    });
  };

  const applyBulkPaste = () => {
    const parsed = parseBulkPastedQuestion(bulkPasteValue);
    if (!parsed.questionText && parsed.options.length === 0) {
      toast.error('Could not detect question and options from the pasted text');
      return;
    }

    const nextOptions = form.options.map((option, index) => ({
      ...option,
      format: 'text',
      text: parsed.options[index] ?? '',
      imageUrl: '',
      audioUrl: '',
    }));

    setForm({
      ...form,
      questionType: null,
      question: {
        ...form.question,
        format: 'text',
        text: parsed.questionText || form.question.text,
        imageUrl: '',
        audioUrl: '',
      },
      options: nextOptions,
    });
    syncFromExternalFill(
      {
        ...form.question,
        format: 'text',
        text: parsed.questionText || form.question.text,
        imageUrl: '',
        audioUrl: '',
      },
      nextOptions,
    );
    toast.success(`Filled question and ${parsed.options.length} options from paste`);
  };

  return (
    <div className="space-y-4">
      {step === 'settings' && (
        <>
          {/* OCR Fill Panel */}
          <OcrFillPanel form={form} setForm={setForm} onApplied={syncFromExternalFill} />
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paste Question + Options
              </label>
              <textarea
                value={bulkPasteValue}
                onChange={(e) => setBulkPasteValue(e.target.value)}
                className="input-field"
                rows={7}
                placeholder={`Question text here\n1. First option\n2. Second option\n3. Third option\n4. Fourth option`}
              />
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={applyBulkPaste} className="btn-secondary">
                Apply Pasted Text
              </button>
              <button type="button" onClick={() => setBulkPasteValue('')} className="btn-secondary">
                Clear
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="w-full flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
              <Settings2 size={14} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Question settings</span>
            </div>
            <div className="px-4 pb-4 pt-3 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-field"
                  placeholder="Optional internal question name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Types</label>
                <div className="flex flex-wrap gap-2">
                  {TYPES.map((type) => {
                    const selected = form.types.includes(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleType(type)}
                        disabled={!!lockedTypes}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
                      >
                        {type.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value, subcategory: '' })
                    }
                    disabled={!!lockedCategory}
                    className="input-field"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                  <select
                    value={form.difficulty}
                    onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                    className="input-field"
                  >
                    {DIFFICULTIES.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Length
                  </label>
                  <select
                    value={form.questionLengthType}
                    onChange={(e) => setForm({ ...form, questionLengthType: e.target.value })}
                    className="input-field"
                  >
                    {QUESTION_LENGTH_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                  <select
                    value={form.tier}
                    onChange={(e) => setForm({ ...form, tier: e.target.value })}
                    className="input-field"
                  >
                    {TIERS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {form.types.includes('ubt') && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Layout</label>
                    <LayoutPreviewPicker
                      value={form.ubtLayout}
                      onChange={(ubtLayout) => setForm({ ...form, ubtLayout })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group{' '}
                      <span className="text-gray-400 font-normal">
                        (siblings sharing the same prompt)
                      </span>
                    </label>
                    <select
                      value={selectedGroupValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setForm({ ...form, groupId: '', ubtPrompt: { stem: '' } });
                        } else if (value === NEW_GROUP) {
                          setForm({ ...form, groupId: genGroupId(), ubtPrompt: { stem: '' } });
                        } else {
                          const picked = groups.find((g) => g.groupId === value);
                          setForm({
                            ...form,
                            groupId: value,
                            ubtPrompt: { stem: picked?.prompt ?? form.ubtPrompt.stem },
                          });
                        }
                      }}
                      className="input-field"
                    >
                      <option value="">No group (standalone question)</option>
                      <option value={NEW_GROUP}>+ Create new group</option>
                      {groups.map((g) => (
                        <option key={g.groupId} value={g.groupId}>
                          {g.prompt ? g.prompt.slice(0, 40) : 'Untitled group'} — {g.count} question
                          {g.count !== 1 ? 's' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Group Prompt (shared stem for grouped questions, e.g.{' '}
                      <code className="font-korean">그림을 보고 맞는 것을 고르십시오.</code>)
                    </label>
                    <UnderlineField
                      multiline
                      rows={2}
                      value={form.ubtPrompt.stem}
                      onChange={(stem) =>
                        setForm({ ...form, ubtPrompt: { ...form.ubtPrompt, stem } })
                      }
                      placeholder={
                        form.groupId
                          ? 'Shared prompt text for the question group'
                          : 'Pick or create a group first'
                      }
                      disabled={!form.groupId}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setStep('preview')}
              className="btn-primary flex items-center gap-1"
            >
              Continue to preview <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}

      {step === 'preview' && (
        <>
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => setStep('settings')}
              className="flex items-center gap-1 text-blue-600 hover:underline"
            >
              <ChevronLeft size={14} /> Back to question settings
            </button>
            <span className="text-gray-400 truncate">{metaSummary}</span>
          </div>

          <ExamPreviewFrame
            left={
              <QuestionStemFields
                blocks={form.question.blocks}
                onBlocksChange={(blocks) =>
                  setForm({ ...form, question: { ...form.question, blocks } })
                }
                textDisabled={form.ubtLayout === 'number_only'}
              />
            }
            right={
              <QuestionOptionsFields
                optionFormat={optionFormat}
                onOptionFormatChange={changeOptionFormat}
                options={form.options}
                onOptionChange={updateOption}
              />
            }
            toolbar={
              <div className="flex items-center gap-1">
                {(
                  [
                    ['b', Bold, 'Bold selected text'],
                    ['i', Italic, 'Italic selected text'],
                    ['u', Underline, 'Underline selected text'],
                  ] as const
                ).map(([tag, Icon, title]) => (
                  <button
                    key={tag}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyTag(tag)}
                    title={title}
                    className="flex items-center justify-center rounded-md border border-gray-300 bg-white p-1 text-gray-600 hover:bg-gray-50"
                  >
                    <Icon size={12} />
                  </button>
                ))}
              </div>
            }
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Explanation (EN)</label>
            <textarea
              value={form.explanation.en}
              onChange={(e) =>
                setForm({ ...form, explanation: { ...form.explanation, en: e.target.value } })
              }
              className="input-field"
              rows={2}
              placeholder="Optional explanation"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="input-field"
              placeholder="grammar, topik, etc."
            />
          </div>

          {blocksValidationError(form) && (
            <p className="flex items-center gap-1.5 text-sm text-amber-700">
              <AlertCircle size={14} /> {blocksValidationError(form)}
            </p>
          )}
        </>
      )}
    </div>
  );
}
